import { useEffect, useMemo, useRef } from 'react';
import { Bubble } from './Bubble.tsx';
import type { ChatMessage } from './types.js';
import { useI18n } from '../i18n/I18n.tsx';

/**
 * The scrolling message area.
 *
 * Two behaviours carry most of the feel:
 *
 *   Grouping — consecutive messages from one sender sit tight together and only
 *   the first carries a tail, so a burst reads as one turn rather than four.
 *
 *   Sticking — the view follows new messages only when already at the bottom.
 *   Yanking someone back while they are reading history is the single most
 *   irritating thing a chat can do.
 */

/** A gap this long starts a new visual group even from the same sender. */
const GROUP_BREAK_MS = 5 * 60 * 1000;

function dayLabel(iso: string, locale: string, todayLabel: string, yesterdayLabel: string): string {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

  if (sameDay(date, today)) return todayLabel;
  if (sameDay(date, yesterday)) return yesterdayLabel;

  // Within the last week, the weekday alone is the most readable form.
  if (today.getTime() - date.getTime() < 7 * 86400000) {
    return date.toLocaleDateString(locale, { weekday: 'long' });
  }
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}

export interface MessageListProps {
  messages: ChatMessage[];
  role: 'owner' | 'visitor';
  /** True when the conversation has more than two participants. */
  showSenders?: boolean;
  /** Rendered above the first message: usually the encryption notice. */
  banner?: React.ReactNode;
  emptyState?: React.ReactNode;
  onOpenAttachment: (message: ChatMessage) => Promise<{ url: string; mimeType: string }>;
}

export function MessageList({
  messages,
  role,
  showSenders,
  banner,
  emptyState,
  onOpenAttachment,
}: MessageListProps) {
  const { t, language } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  const onScroll = () => {
    const node = ref.current;
    if (!node) return;
    stick.current = node.scrollHeight - node.scrollTop - node.clientHeight < 120;
  };

  useEffect(() => {
    const node = ref.current;
    if (node && stick.current) node.scrollTop = node.scrollHeight;
  }, [messages.length]);

  /** Days, and within each day, runs by the same sender. */
  const days = useMemo(() => {
    const out: Array<{ day: string; items: Array<{ message: ChatMessage; groupStart: boolean }> }> =
      [];
    let previous: ChatMessage | undefined;

    for (const message of messages) {
      const day = dayLabel(message.createdAt, language, t('chat.today'), t('chat.yesterday'));
      let bucket = out[out.length - 1];
      if (!bucket || bucket.day !== day) {
        bucket = { day, items: [] };
        out.push(bucket);
        previous = undefined;
      }

      const gap = previous
        ? new Date(message.createdAt).getTime() - new Date(previous.createdAt).getTime()
        : Infinity;
      const groupStart = !previous || previous.sender !== message.sender || gap > GROUP_BREAK_MS;

      bucket.items.push({ message, groupStart });
      previous = message;
    }
    return out;
  }, [messages, language, t]);

  return (
    <div className="msg-scroll" ref={ref} onScroll={onScroll}>
      {/* Pushes short conversations to the bottom, as a messenger does. */}
      <div className="msg-scroll__spacer" />

      {banner}

      {messages.length === 0 ? emptyState : null}

      {days.map((day) => (
        <div key={day.day} style={{ display: 'contents' }}>
          <div className="day-sep">{day.day}</div>
          {day.items.map(({ message, groupStart }) => (
            <Bubble
              key={message.id}
              message={message}
              mine={message.sender === role}
              groupStart={groupStart}
              showSender={Boolean(showSenders) && message.sender !== role}
              onOpenAttachment={onOpenAttachment}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
